/**
 * apps/battle/client/src/StudyDrawer.tsx — Phase 4.2 M4 Study panel.
 *
 * Read-only right-side drawer that loads /api/study/concepts for the current
 * question's conceptTags and renders every non-empty body field in canonical
 * order. First concept is expanded by default; the rest are collapsed.
 *
 * Layout:
 *   - ≥900 px viewport: ~40% width fixed drawer; battle remains interactive.
 *   - <900 px viewport: full-screen overlay (CSS-only via media query).
 *
 * Closing: ✕ button, Escape key. Click-outside is intentionally a no-op.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from './api.js';
import type { StudyConcept, StudyConceptBody, StudyConceptsResp } from './api.js';
import { Spinner } from './widgets.js';

interface StudyDrawerProps {
  open: boolean;
  conceptCodes: string[];
  onClose: () => void;
}

/** Canonical render order for body fields (matches DB audit, June 2026). */
const BODY_FIELD_ORDER: Array<{
  key: keyof StudyConceptBody;
  label: string;
  kind: 'paragraph' | 'list' | 'worked' | 'link';
}> = [
  { key: 'examiner_definition', label: 'Definition', kind: 'paragraph' },
  { key: 'key_distinctions', label: 'Key distinctions', kind: 'list' },
  { key: 'how_tested', label: 'How it’s tested', kind: 'paragraph' },
  { key: 'diagram_notes', label: 'Diagram notes', kind: 'paragraph' },
  { key: 'worked_examples', label: 'Worked examples', kind: 'worked' },
  { key: 'applications', label: 'Applications', kind: 'list' },
  { key: 'exam_link', label: 'Exam link', kind: 'link' },
];

function isNonEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim()) as string[];
  if (typeof v === 'string' && v.trim()) return [v.trim()];
  return [];
}

function renderField(
  field: (typeof BODY_FIELD_ORDER)[number],
  value: unknown,
): React.ReactNode {
  switch (field.kind) {
    case 'paragraph': {
      const text = typeof value === 'string' ? value : '';
      if (!text.trim()) return null;
      return <p className="study-paragraph">{text}</p>;
    }
    case 'list': {
      const items = asStringArray(value);
      if (items.length === 0) return null;
      return (
        <ul className="study-list">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }
    case 'worked': {
      if (!Array.isArray(value) || value.length === 0) return null;
      return (
        <ol className="study-worked">
          {value.map((ex, i) => {
            if (typeof ex === 'string') {
              return <li key={i}><div className="study-worked-body">{ex}</div></li>;
            }
            const prompt = typeof ex?.prompt === 'string' ? ex.prompt : '';
            const solution = typeof ex?.solution === 'string' ? ex.solution : '';
            if (!prompt && !solution) return null;
            return (
              <li key={i}>
                {prompt && <div className="study-worked-prompt">{prompt}</div>}
                {solution && (
                  <div className="study-worked-solution">
                    <strong>Solution:</strong> {solution}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      );
    }
    case 'link': {
      const text = typeof value === 'string' ? value.trim() : '';
      if (!text) return null;
      // Treat as link if it parses as URL; otherwise plain text.
      const isUrl = /^https?:\/\//i.test(text);
      return isUrl ? (
        <a className="study-link" href={text} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ) : (
        <p className="study-paragraph">{text}</p>
      );
    }
  }
}

interface ConceptSectionProps {
  concept: StudyConcept;
  defaultExpanded: boolean;
}

function ConceptSection({ concept, defaultExpanded }: ConceptSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const body = (concept.body ?? {}) as StudyConceptBody;

  const sections = BODY_FIELD_ORDER.map((field) => {
    const value = body[field.key];
    if (!isNonEmpty(value)) return null;
    const node = renderField(field, value);
    if (!node) return null;
    return (
      <div key={String(field.key)} className="study-section">
        <h4 className="study-section-h">{field.label}</h4>
        {node}
      </div>
    );
  }).filter(Boolean);

  const hasSpec = isNonEmpty(concept.specRequirement);
  const hasCoreDef = isNonEmpty(concept.coreDefinition);
  const hasSmeNotes = isNonEmpty(concept.smeNotes);
  const nothing = sections.length === 0 && !hasSpec && !hasCoreDef && !hasSmeNotes;

  return (
    <div className="study-concept" data-testid={`study-concept-${concept.conceptCode}`}>
      <button
        type="button"
        className="study-concept-header"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="study-concept-code">{concept.conceptCode}</span>
        <span className="study-concept-title">· {concept.conceptTitle || '(untitled)'}</span>
        <span className="study-concept-chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div className="study-concept-body">
          {hasSpec && (
            <div className="study-section">
              <h4 className="study-section-h">Specification</h4>
              <p className="study-paragraph">{concept.specRequirement}</p>
            </div>
          )}
          {hasCoreDef && (
            <div className="study-section">
              <h4 className="study-section-h">Core definition</h4>
              <p className="study-paragraph">{concept.coreDefinition}</p>
            </div>
          )}
          {sections}
          {hasSmeNotes && (
            <div className="study-section study-sme-notes">
              <h4 className="study-section-h">SME notes</h4>
              <p className="study-paragraph">{concept.smeNotes}</p>
            </div>
          )}
          {nothing && (
            <p className="study-empty">No notes available for this concept yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

export function StudyDrawer({ open, conceptCodes, onClose }: StudyDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [concepts, setConcepts] = useState<StudyConcept[]>([]);
  const [missing, setMissing] = useState<string[]>([]);
  const lastKeyRef = useRef<string>('');

  const codesKey = useMemo(() => conceptCodes.join(','), [conceptCodes]);

  // Fetch on open + when codes change.
  useEffect(() => {
    if (!open) return;
    if (!codesKey) {
      setConcepts([]);
      setMissing([]);
      setError(null);
      return;
    }
    // Avoid duplicate fetch when re-opening with the same codes.
    if (lastKeyRef.current === codesKey && concepts.length > 0) return;
    lastKeyRef.current = codesKey;

    let aborted = false;
    setLoading(true);
    setError(null);
    setConcepts([]);
    setMissing([]);

    api
      .get<StudyConceptsResp>(`/api/study/concepts?codes=${encodeURIComponent(codesKey)}`)
      .then((resp) => {
        if (aborted) return;
        setConcepts(resp.concepts ?? []);
        setMissing(resp.missing ?? []);
      })
      .catch((err) => {
        if (aborted) return;
        const msg =
          err instanceof ApiError ? `Couldn’t load study notes (${err.status}).` : 'Couldn’t load study notes.';
        setError(msg);
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });

    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, codesKey]);

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  function retry() {
    lastKeyRef.current = ''; // force re-fetch
    // Trigger the effect by toggling state — easiest is to update codesKey;
    // since codes don't change, set error null + loading true, then re-call.
    if (!codesKey) return;
    setError(null);
    setLoading(true);
    api
      .get<StudyConceptsResp>(`/api/study/concepts?codes=${encodeURIComponent(codesKey)}`)
      .then((resp) => {
        setConcepts(resp.concepts ?? []);
        setMissing(resp.missing ?? []);
      })
      .catch((err) => {
        const msg =
          err instanceof ApiError ? `Couldn’t load study notes (${err.status}).` : 'Couldn’t load study notes.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }

  return (
    <aside
      className="study-drawer"
      role="dialog"
      aria-modal="false"
      aria-label="Study notes"
      data-testid="study-drawer"
    >
      <header className="study-drawer-h">
        <div className="study-drawer-title">Study notes</div>
        <button
          type="button"
          className="study-drawer-close"
          aria-label="Close study notes"
          onClick={onClose}
          data-testid="button-study-close"
        >
          ✕
        </button>
      </header>

      <div className="study-drawer-body">
        {loading && (
          <div className="study-loading"><Spinner size={20} /></div>
        )}
        {!loading && error && (
          <div className="study-error">
            <p>{error}</p>
            <button type="button" className="btn btn-secondary" onClick={retry}>
              Try again
            </button>
          </div>
        )}
        {!loading && !error && concepts.length === 0 && (
          <p className="study-empty">No study notes available for this question.</p>
        )}
        {!loading && !error && concepts.map((c, i) => (
          <ConceptSection key={c.conceptCode} concept={c} defaultExpanded={i === 0} />
        ))}
        {!loading && !error && missing.length > 0 && (
          <p className="study-missing">
            Missing notes: {missing.join(', ')}
          </p>
        )}
      </div>
    </aside>
  );
}
